import cx from 'classnames'
import {
  BaseSyntheticEvent,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import XRegExp from 'xregexp'

import {
  SanityPrintAreaType,
  SanityPrintFeeCombination,
  SanityPrintPermission,
} from '@data/sanity/queries/types/print'
import { SanityPrintColor } from '@data/sanity/queries/types/shop'
import { SanityPrintConfiguratorStrings } from '@data/sanity/queries/types/strings'
import { useGetFormattedPrice } from '@lib/helpers'
import { getPrintFeePrice } from '@lib/print/fee'
import {
  freetextRegex,
  getPrintColorOptions,
  getPrintFreetextInputLabel,
} from '@lib/print/text'
import { SlotConfiguration, SlotWithConfiguration } from '@lib/print/types'
import { StringsContext } from '@lib/strings-context'

import Checkbox from '@components/checkbox/alternative'
import RadioGroup from '@components/radio-group'
import RadioGroupItem from '@components/radio-group-item'

interface PrintConfiguratorSlotTextFieldProps {
  printConfiguratorStrings: SanityPrintConfiguratorStrings
  slotsWithConfiguration: SlotWithConfiguration[]
  printFeeCombinations: SanityPrintFeeCombination[]
  activeSlotIndex: number
  imageAreaTypes: SanityPrintAreaType[]
  permissions?: SanityPrintPermission | null
  onConfigurationChange: (
    slotId: string,
    newConfiguration: Partial<SlotConfiguration>,
  ) => void
}

const PrintConfiguratorSlotTextField = ({
  printConfiguratorStrings,
  slotsWithConfiguration,
  printFeeCombinations,
  activeSlotIndex,
  imageAreaTypes,
  permissions,
  onConfigurationChange,
}: PrintConfiguratorSlotTextFieldProps) => {
  const strings = useContext(StringsContext)

  const [freetext, setFreetext] = useState('')

  const slotIdRef = useRef<string | null>(null)

  const textInputId = useId()
  const getFormattedPrice = useGetFormattedPrice()

  const activeSlot = useMemo(() => {
    return slotsWithConfiguration[activeSlotIndex]
  }, [activeSlotIndex, slotsWithConfiguration])

  const textPrintFeePrice = useMemo(() => {
    return getPrintFeePrice(
      printFeeCombinations,
      slotsWithConfiguration,
      activeSlotIndex,
      'text',
    )
  }, [activeSlotIndex, printFeeCombinations, slotsWithConfiguration])

  const fieldTitle = useMemo(() => {
    return `${printConfiguratorStrings.printConfiguratorFreetextTitle} ${
      typeof textPrintFeePrice !== 'undefined'
        ? ` (+${getFormattedPrice(textPrintFeePrice)})`
        : ''
    }`
  }, [getFormattedPrice, printConfiguratorStrings, textPrintFeePrice])

  const handleToggleFreetext = (newChecked: boolean) => {
    if (newChecked) {
      // Show freetext in print preview and restore field value
      onConfigurationChange(activeSlot._id, {
        freetextEnabled: 'yes',
        freetext,
      })
    }

    if (!newChecked) {
      // Hide freetext from print preview and clear field value
      onConfigurationChange(activeSlot._id, {
        freetextEnabled: 'no',
        freetext: '',
      })
    }
  }

  const handleFreetextChange = (event: BaseSyntheticEvent) => {
    let newValue = event.target.value

    // Remove emojis and some special characters
    let selectionStart = event.target.selectionStart
    let selectionEnd = event.target.selectionEnd

    if (XRegExp.test(newValue, freetextRegex)) {
      newValue = XRegExp.replace(newValue, freetextRegex, '')
      selectionStart -= 1
      selectionEnd -= 1
    }

    onConfigurationChange(activeSlot._id, {
      freetext: newValue,
    })

    // Wait for input value to update, then move cursor to previous position
    setTimeout(() => {
      event.target.setSelectionRange(selectionStart, selectionEnd)
    }, 0)
  }

  // Load freetext value when slot changes
  useEffect(() => {
    // Don't reset freetext if slot doesn't change
    if (slotIdRef.current === activeSlot._id) {
      return
    }

    slotIdRef.current = activeSlot._id

    setFreetext(activeSlot.configuration.freetext)
  }, [activeSlot, imageAreaTypes, permissions])

  const printColorOptions = getPrintColorOptions(strings)
  const printFreetextInputLabel = getPrintFreetextInputLabel(
    printConfiguratorStrings,
    activeSlot.type,
  )
  const errorMessage = !activeSlot.configuration.freetext
    ? printConfiguratorStrings.printConfiguratorFreetextInputEmptyMessage
    : undefined

  return (
    <div className="flex flex-col gap-y-5">
      <Checkbox
        checked={activeSlot.configuration.freetextEnabled === 'yes'}
        setChecked={handleToggleFreetext}
      >
        <div className="leading-tight font-semibold">{fieldTitle}</div>
      </Checkbox>

      {activeSlot.configuration.freetextEnabled === 'yes' && (
        <>
          <div className="grid max-w-[400px]">
            <div className="flex flex-col gap-y-2">
              {!!printFreetextInputLabel && (
                <label
                  htmlFor={textInputId}
                  className="leading-tight font-semibold text-sm"
                >
                  {printFreetextInputLabel}
                </label>
              )}

              <div
                className={cx('border rounded bg-white', {
                  'border-error': errorMessage,
                  'border-gray-light': !errorMessage,
                })}
              >
                <input
                  id={textInputId}
                  type="text"
                  maxLength={20}
                  value={freetext}
                  onChange={(event) => {
                    if (freetext !== event.target.value) {
                      setFreetext(event.target.value)
                    }

                    handleFreetextChange(event)
                  }}
                  onBlur={(event) => {
                    if (freetext !== event.target.value) {
                      setFreetext(event.target.value)
                    }
                  }}
                  className={cx(
                    'appearance-none w-full h-full',
                    'px-4 py-2 text-base leading-snug',
                    'bg-input-bg text-input-text placeholder-input-placeholder',
                  )}
                />
              </div>

              {!!errorMessage && (
                <span
                  role="alert"
                  className="leading-tight font-semibold text-sm text-error"
                >
                  {errorMessage}
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-y-4">
            <p className="leading-tight font-semibold text-base">
              {printConfiguratorStrings.printConfiguratorFreetextColorTitle}
            </p>

            <RadioGroup
              value={activeSlot.configuration.freetextColor}
              onChange={(value) => {
                onConfigurationChange(activeSlot._id, {
                  freetextColor: value as SanityPrintColor,
                })
              }}
              className="flex items-center flex-wrap gap-5"
            >
              {printColorOptions.map((item) => {
                const isSelected =
                  item.value === activeSlot.configuration.freetextColor

                return (
                  <RadioGroupItem
                    key={item.value}
                    value={item.value}
                    className={cx(
                      'flex items-center gap-x-4 p-1.5 pr-5 border rounded-full',
                      {
                        'border-gray-light': !isSelected,
                        'border-black': isSelected,
                      },
                    )}
                  >
                    <span
                      className={cx(
                        'flex items-center justify-center w-[26px] h-[26px] rounded-full border',
                        item.dotClassName,
                      )}
                    />
                    <p className="leading-snug font-medium text-base">
                      {item.label}
                    </p>
                  </RadioGroupItem>
                )
              })}
            </RadioGroup>
          </div>
        </>
      )}
    </div>
  )
}

export default PrintConfiguratorSlotTextField
